/* import __COLOCATED_TEMPLATE__ from './copilot-adapter.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
/* eslint-disable no-console */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { type PublicAPI } from 'embercom/components/inbox2/conversation-reply-composer';
import {
  BlocksDocument,
  BaseConfig,
  type ComposerConfig,
  type ComposerPublicAPI,
} from '@intercom/composer-ember';
import { type AiAssistPromptKey } from 'embercom/resources/inbox2/composer/ai-assist';
import AiAssistApi from 'embercom/resources/inbox2/composer/ai-assist';
import { type BlockList } from '@intercom/interblocks.ts';
import type CopilotApi from 'embercom/services/copilot-api';
import IntercomAdapter, {
  FrameInwardEvents,
  FrameOutwardEvents,
  type FrameComposerType,
} from 'embercom/lib/frame-adapter';
import type ThemeService from 'embercom/services/theme-service';

//@ts-ignore
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';
import Theme from 'embercom/objects/inbox/types/theme';
import type CopilotFrameService from 'embercom/services/copilot-frame-service';
import { enqueueTask } from 'ember-concurrency-decorators';
import { timeout } from 'ember-concurrency';
import ENV from 'embercom/config/environment';
import { isPresent } from '@ember/utils';
import { taskFor } from 'ember-concurrency-ts';
import { getOwner } from '@ember/application';
import type ApplicationInstance from '@ember/application/instance';
import { StandalonePlatform } from 'embercom/objects/standalone/constants';
import { htmlToTextContent } from 'embercom/lib/html-unescape';
import type IntlService from 'ember-intl/services/intl';

interface Args {
  externalId?: string;
  externalType?: keyof typeof COPILOT_EXTERNAL_TYPE_MAPPING;
  updateParams: (paramName: string, paramValue: string) => void;
}

export const COPILOT_EXTERNAL_TYPE_MAPPING = {
  PREVIEW_MODE: -1,
  ZENDESK_TICKET: 1,
  SALESFORCE_CASE: 6,
  SALESFORCE_IN_APP_MESSAGE_CONVERSATION: 31,
  NEW_ZENDESK_TICKET: 34,
} as const;

export default class CopilotAdapter extends Component<Args> {
  @tracked declare composerApi: PublicAPI;
  @tracked blocksDoc: BlocksDocument;
  @tracked composerConfig: ComposerConfig;
  @service declare appService: AppService;
  @service declare copilotApi: CopilotApi;
  @service declare themeService: ThemeService;
  @service declare copilotFrameService: CopilotFrameService;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: IntlService;

  declare intercomAdapter: IntercomAdapter;

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    this.blocksDoc = new BlocksDocument();
    this.composerConfig = {
      ...new BaseConfig(),
    };

    this.copilotApi.setExternalReference(
      args.externalId,
      this.getExternalTypeMapping(args.externalType),
    );

    // Initialize the IntercomAdapter
    console.log('initializing intercom adapter');
    this.intercomAdapter = new IntercomAdapter({
      owner: getOwner(this) as ApplicationInstance,
      listenWindow: window,
      sendWindow: window.parent,
    });

    // Set up event listeners using the adapter
    this.intercomAdapter.on(FrameInwardEvents.IC_FRAME_CONVERSATION_CHANGED, (payload: any) => {
      console.log('Conversation changed payload:', payload);
      let { external_id, external_type } = payload;
      this.copilotApi.setExternalReference(external_id, this.getExternalTypeMapping(external_type));
    });

    if (this.appService.app.hasStandalonePlatform(StandalonePlatform.Salesforce)) {
      this.intercomAdapter.on(
        FrameInwardEvents.IC_FRAME_SALESFORCE_COMPOSER_VISIBILITY_STATE,
        (isExternalComposerVisible: boolean) => {
          console.log('Composer visibility state changed to:', isExternalComposerVisible);
          this.copilotFrameService.composerVisible = isExternalComposerVisible;
        },
      );
    }

    this.intercomAdapter.on(FrameInwardEvents.IC_FRAME_COLOR_SCHEME_CHANGED, (payload: any) => {
      console.log('Color scheme changed payload:', payload);

      if (payload.theme === 'light') {
        this.themeService.setForcedTheme(Theme.Light);
      } else {
        this.themeService.setForcedTheme(Theme.Dark);
      }
    });

    this.getComposerVisibilityState();
    taskFor(this.periodicExternalComposerStateCheck).perform();
  }

  private getComposerVisibilityState() {
    if (this.appService.app.hasStandalonePlatform(StandalonePlatform.Salesforce)) {
      // emits a message and the parent window will send a response via IC_FRAME_SALESFORCE_COMPOSER_VISIBILITY_STATE event
      this.intercomAdapter.send(
        FrameOutwardEvents.IC_FRAME_GET_SALESFORCE_COMPOSER_VISIBILITY_STATE,
        {},
      );
    }
  }

  @enqueueTask *periodicExternalComposerStateCheck() {
    yield timeout(ENV.APP._1000MS);

    let externalComposerContent: { text: string | null; type: FrameComposerType } =
      yield this.getExternalComposerContent();

    this.copilotFrameService.composerHasContent = isPresent(externalComposerContent.text);
    this.copilotFrameService.composerType = externalComposerContent.type;

    taskFor(this.periodicExternalComposerStateCheck).perform();
  }

  willDestroy() {
    super.willDestroy();
    // Clean up the adapter when component is destroyed
    this.intercomAdapter.destroy();
  }

  getExternalTypeMapping(type?: string) {
    return this.typeMapping[type as keyof typeof this.typeMapping];
  }

  typeMapping = COPILOT_EXTERNAL_TYPE_MAPPING;

  @action async getExternalComposerContent() {
    return this.intercomAdapter.send(
      FrameOutwardEvents.IC_FRAME_GET_CURRENT_COMPOSER_CONTENT,
      {},
      FrameInwardEvents.IC_FRAME_CURRENT_COMPOSER_CONTENT,
    );
  }

  @action setComposerApi(api: ComposerPublicAPI) {
    this.composerApi = {
      focus: () => {
        console.log('composer api focus');
      },
      setPane: () => {
        console.log('composer api setPane');
      },
      setActiveReplyPane: () => {
        console.log('composer api setActiveReplyPane');

        return this.blocksDoc.blocks;
      },
      insertBlocks: () => {
        console.log('composer api insertBlocks');
      },
      replaceBlocks: (blocks: BlockList) => {
        console.log('composer api replaceBlocks', blocks);
        this.addBlocksToComposer(blocks);
      },
      setActiveNotePane: () => {
        console.log('composer api setActiveNotePane');
        return this.blocksDoc.blocks;
      },
      insertMacroActions: () => {
        console.log('composer api insertMacroActions');
      },
      api,
      aiAssistCompletion: this.aiAssistCompletion.bind(this),
    };
  }

  @action addBlocksToComposer(blocks: any[]) {
    this.updateBlocks(blocks);

    try {
      this.composerApi.api.composer.commands.replaceAllWithBlocks(blocks);
    } catch (error) {
      console.error('Error adding blocks to our internal composer', error);
    }

    let htmlText = this.blocksToHtml(blocks);
    this.sendComposerContentToParent('public', htmlText);
  }

  private blocksToHtml(blocks: any[]): string {
    // we have an renderBlocksV1 helper that can convert blocks to html
    // but the resulted html would have divs and other extra html elements
    // that salesforce case composer does not like
    // so we're doing our own naive conversion here
    let entries = this.blocksToTextEntries(blocks.reject((block) => block.type === 'sources'));
    let text = this.concatenateTextEntries(entries);

    text = this.trimQuotes(text);
    text = this.sanitizeEmptyInlineCitations(text);
    text = sanitizeHtml(text).string;

    return text;
  }

  private blocksToTextEntries(blocks: any[]): { type: string; content: string }[] {
    return blocks.map((block) => {
      return { type: block.type, content: this.textContentForBlock(block) };
    });
  }

  private textContentForBlock(block: any): string {
    if (block.items && block.type === 'unorderedList') {
      return block.items.map((item: any) => `• ${item}`).join('<br>');
    } else if (block.items && block.type === 'orderedList') {
      return block.items.map((item: any, index: number) => `${index + 1}. ${item}`).join('<br>');
    } else {
      return block.text ?? '';
    }
  }

  private concatenateTextEntries(entries: { type: string; content: string }[]): string {
    return entries.reduce((text, entry, index) => {
      if (index > 0) {
        // connect the blocks of the same type with a <br>
        // connect the blocks of different types with a <br><br>
        // this is handy when we concatenate two subsequent paragraph blocks
        // if we need a double break breaks between paragraphs we generally get a third empty paragraph item.
        text += entries[index - 1].type === entry.type ? '<br>' : '<br><br>';
      }

      return text + entry.content;
    }, '');
  }

  private trimQuotes(text: string): string {
    if (text.startsWith('"') && text.endsWith('"')) {
      text = text.substring(1, text.length - 1);
    }
    return text;
  }

  private htmlToPlainText(htmlText: string): string {
    let text = htmlText.replace(/<br>/gi, '\n'); // htmlToTextContent doesn't necessarily add a new line for br tags

    return htmlToTextContent(text);
  }

  sanitizeEmptyInlineCitations(input: string): string {
    return input.replace(/<a[^>]*class="inline-citation"[^>]*><\/a>/g, '');
  }

  @action async aiComposeForExternalContent(func: (blocks: BlockList) => Promise<any>) {
    let externalContent = (await this.getExternalComposerContent()) as { text: string | null };

    if (!externalContent?.text) {
      console.log('No external composer content found');
      return;
    }

    let blocks = [{ type: 'paragraph', text: externalContent.text }];

    let aiComposeResult = await func(blocks);
    let htmlText = this.blocksToHtml(aiComposeResult);

    this.sendComposerContentToParent('generic', htmlText);
  }

  @action addNoteBlocks(blocks: any[]) {
    let htmlText = this.blocksToHtml(blocks);

    this.sendComposerContentToParent('private', htmlText);
  }

  async sendComposerContentToParent(type: 'public' | 'private' | 'generic', text: string) {
    if (!this.copilotFrameService.composerVisible) {
      await this.copyToClipboard(this.htmlToPlainText(text));
      return;
    }

    this.intercomAdapter.send(this.composerContentEventTypeFor(type), {
      text,
      plainText: this.htmlToPlainText(text),
    });
  }

  private async copyToClipboard(text: string): Promise<void> {
    if (!navigator.clipboard) {
      // Check if clipboard API is available
      console.warn('Clipboard API is not available');
      this.notificationsService.notifyWarning(
        this.intl.t('standalone.copilot-frame.copy-to-clipboard.navigator-not-available'),
      );
      return;
    }

    try {
      // Check clipboard-write permission
      let permission = await this.checkClipboardPermission();

      if (permission === 'denied') {
        this.notificationsService.notifyWarning(
          this.intl.t('standalone.copilot-frame.copy-to-clipboard.access-denied'),
          // 'Clipboard access was denied. Please check your browser permissions.',
        );
        return;
      }

      // Attempt to write to clipboard
      await navigator.clipboard.writeText(text);

      // Show success feedback
      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.copilot-frame.copy-to-clipboard.success'),
      );
    } catch (error) {
      console.warn('Failed to copy to clipboard:', error);
      this.notificationsService.notifyWarning(
        this.intl.t('standalone.copilot-frame.copy-to-clipboard.failed'),
      );
    }
  }

  /**
   * Check clipboard-write permission status
   */
  private async checkClipboardPermission(): Promise<PermissionState> {
    try {
      if (!navigator.permissions) {
        // Check if Permissions API is available
        // Assume granted if we can't check
        console.warn('Permissions API is not available');

        return 'granted';
      }

      let permission = await navigator.permissions.query({
        name: 'clipboard-write' as PermissionName,
      });
      return permission.state;
    } catch (error) {
      console.warn('clipboard-write permission is not queryable', error);
      return 'granted'; // Assume granted if we can't check
    }
  }

  private composerContentEventTypeFor(type: 'public' | 'private' | 'generic') {
    if (type === 'public') {
      return FrameOutwardEvents.IC_FRAME_ADD_COMPOSER_PUBLIC_CONTENT;
    } else if (type === 'private') {
      return FrameOutwardEvents.IC_FRAME_ADD_COMPOSER_PRIVATE_CONTENT;
    } else {
      return FrameOutwardEvents.IC_FRAME_ADD_COMPOSER_CONTENT;
    }
  }

  @action updateBlocks(blocks: any[]) {
    this.blocksDoc.blocks = blocks;
  }

  private aiAssistApi = AiAssistApi.from(this, () => {
    return {
      composerApi: this.composerApi?.api,
      currentBlocks: this.blocksDoc.blocks,
      conversationId: '-1',
    };
  });

  get isRunningAiAssist() {
    return this.aiAssistApi?.isWorking;
  }

  @action async generateSummaryForExternalConversation() {
    try {
      let response = await this.aiAssistApi.generateSummaryForExternalConversation();
      this.addNoteBlocks(response.summary);
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t(
          `standalone.copilot-frame.failed-to-generate-summary.${this.conversationTypeTranslationKey}`,
        ),
      );
      throw error;
    }
  }

  get conversationTypeTranslationKey() {
    switch (this.copilotApi.externalType) {
      case COPILOT_EXTERNAL_TYPE_MAPPING.ZENDESK_TICKET:
      case COPILOT_EXTERNAL_TYPE_MAPPING.NEW_ZENDESK_TICKET:
        return 'ticket';
      case COPILOT_EXTERNAL_TYPE_MAPPING.SALESFORCE_CASE:
        return 'case';
      case COPILOT_EXTERNAL_TYPE_MAPPING.SALESFORCE_IN_APP_MESSAGE_CONVERSATION:
        return 'messaging-session';
      case COPILOT_EXTERNAL_TYPE_MAPPING.PREVIEW_MODE:
        return 'conversation';
      default:
        return 'conversation';
    }
  }

  @action aiAssistCompletion(promptKey: AiAssistPromptKey, blocks: BlockList) {
    return this.aiAssistApi.complete(
      promptKey,
      undefined, // conversation id is undefined for external conversations
      blocks,
      {
        action: 'replace_selection',
        section: 'composer',
        object: 'composer',
        source: 'composer_toolbar',
      },
      '-1', // This should be the user ID
      { isRetrying: false },
      this.copilotApi.externalId,
      this.copilotApi.externalType,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Adapters::CopilotAdapter': typeof CopilotAdapter;
  }
}
